import { AlertTriangle, Download, FileText, Landmark, Link2, Send, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Shell } from "../components/Shell";
import { StatusPill } from "../components/StatusPill";
import {
  clients as clientsApi,
  compliance as complianceApi,
  filings as filingsApi,
  type Client,
  type ComplianceDoc,
  type Filing,
  type FilingKind,
  type FtcItemDraft,
} from "../lib/api";
import { collectorWarningText, type CollectorComplaintDraft } from "../lib/api";

const BUREAU_LABEL: Record<string, string> = { equifax: "Equifax", experian: "Experian", transunion: "TransUnion" };

function statusLabel(s: Filing["draft_status"]): string {
  return { drafting: "Drafting", client_review: "Client review", submitted: "Submitted", rejected: "Rejected" }[s] ?? s;
}

export default function CompliancePage() {
  const [docs, setDocs] = useState<ComplianceDoc[]>([]);
  const [filings, setFilings] = useState<Filing[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  // Prepare form
  const [clientId, setClientId] = useState("");
  const [kind, setKind] = useState<FilingKind>("cfpb_complaint");
  const [preparing, setPreparing] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Open filing (review + file)
  const [open, setOpen] = useState<Filing | null>(null);
  const [narrative, setNarrative] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // FTC IdentityTheft.gov flow (server-driven, phone-OTP relay)
  const [ftcItems, setFtcItems] = useState<FtcItemDraft[]>([]);
  const [ftcPhone, setFtcPhone] = useState("");
  const [ftcAttest, setFtcAttest] = useState(false);
  const [ftcCode, setFtcCode] = useState("");
  const [ftcBusy, setFtcBusy] = useState(false);
  const [ftcMsg, setFtcMsg] = useState<string | null>(null);
  const [ftcAwaiting, setFtcAwaiting] = useState(false);
  const [ftcRef, setFtcRef] = useState<string | null>(null);

  async function refresh() {
    try { setFilings(await filingsApi.list()); } catch {}
  }

  useEffect(() => {
    (async () => {
      try { setDocs(await complianceApi.list()); } catch {}
      try { setFilings(await filingsApi.list()); } catch {}
      try { setClients((await clientsApi.list({ limit: 200 })).items); } catch {}
      setLoading(false);
    })();
  }, []);

  const draft = open?.draft_content;
  const ftcDraft = (open?.draft_content ?? {}) as any;
  const blockers = draft?.blockers ?? [];

  // Seed the FTC editor whenever a different filing opens.
  useEffect(() => {
    if (open?.kind === "ftc_identity_theft") {
      const d = (open.draft_content ?? {}) as any;
      setFtcItems(Array.isArray(d.items) ? d.items : []);
      setFtcAwaiting(!!d.session_id && !d.reference_number);
      setFtcRef(d.reference_number ?? null);
      setFtcCode(""); setFtcMsg(null); setFtcAttest(false); setFtcPhone("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open?.id]);

  function updItem(i: number, patch: Partial<FtcItemDraft>) {
    setFtcItems((items) => items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }
  function addItem() { setFtcItems((items) => [...items, { company: "" }]); }
  function removeItem(i: number) { setFtcItems((items) => items.filter((_, idx) => idx !== i)); }

  async function ftcStart() {
    if (!open) return;
    setFtcBusy(true); setFtcMsg(null);
    try {
      const r = await filingsApi.ftcStart(open.id, {
        attestation_confirmed: ftcAttest,
        items: ftcItems,
        phone_override: ftcPhone || undefined,
      });
      setFtcAwaiting(true);
      setFtcMsg(r.message);
      await refresh();
    } catch (e: any) {
      setFtcMsg(e?.message || "Could not start the FTC report.");
    } finally { setFtcBusy(false); }
  }

  async function ftcVerify() {
    if (!open) return;
    setFtcBusy(true); setFtcMsg(null);
    try {
      const r = await filingsApi.ftcVerify(open.id, ftcCode);
      setFtcRef(r.reference_number);
      setFtcAwaiting(false);
      setFtcMsg(r.reference_number ? `Report finalized — reference ${r.reference_number}.` : "Report finalized.");
      try { setOpen(await filingsApi.get(open.id)); } catch {}
      await refresh();
    } catch (e: any) {
      setFtcMsg(e?.message || "Could not finish the FTC report.");
    } finally { setFtcBusy(false); }
  }

  async function onPrepare() {
    if (!clientId) { setErr("Pick a client first."); return; }
    setErr(null); setPreparing(true); setLinkUrl(null);
    try {
      const { filing, draft_content } = await filingsApi.prepare(clientId, kind);
      const full = { ...filing, draft_content, client_name: clients.find((c) => c.id === clientId)?.legal_name };
      setOpen(full);
      setNarrative(draft_content.narrative ?? "");
      setConfirmation("");
      await refresh();
    } catch (e: any) {
      setErr(e?.message || "Could not prepare the filing.");
    } finally {
      setPreparing(false);
    }
  }

  async function openFiling(id: string) {
    setErr(null); setLinkUrl(null);
    try {
      const f = await filingsApi.get(id);
      setOpen(f);
      setNarrative(f.draft_content?.narrative ?? "");
      setConfirmation(f.confirmation_number ?? "");
    } catch (e: any) { setErr(e?.message || "Could not load filing."); }
  }

  // Collector complaints are computed from recorded facts; staff can't edit the
  // text or the yes/no answers, only re-pull them after something changes.
  async function refreshCollectorFacts() {
    if (!open) return;
    setSaving(true); setErr(null);
    try {
      const f = await filingsApi.patch(open.id, { refresh_facts: true });
      setOpen({ ...open, draft_content: f.draft_content, draft_status: f.draft_status });
    } catch (e: any) { setErr(e?.body?.message || e?.message || "Could not refresh the complaint."); }
    finally { setSaving(false); }
  }

  async function saveNarrative() {
    if (!open) return;
    setSaving(true);
    try {
      const f = await filingsApi.patch(open.id, { narrative, resolution: narrative });
      setOpen({ ...open, draft_content: f.draft_content });
    } catch (e: any) { setErr(e?.message || "Save failed."); }
    finally { setSaving(false); }
  }

  async function genLink() {
    if (!open) return;
    setSaving(true);
    try {
      const link = await filingsApi.generateLink(open.id, "manual_link");
      setLinkUrl(link.public_url);
      await refresh();
    } catch (e: any) { setErr(e?.message || "Could not generate link."); }
    finally { setSaving(false); }
  }

  async function markFiled() {
    if (!open) return;
    setSaving(true);
    try {
      const f = await filingsApi.submit(open.id, { confirmation_number: confirmation || undefined, operator_assisted: true });
      setOpen({ ...open, ...f });
      await refresh();
    } catch (e: any) { setErr(e?.message || "Could not log submission."); }
    finally { setSaving(false); }
  }

  const clientOptions = useMemo(
    () => clients.filter((c) => c.status !== "churned").sort((a, b) => (a.legal_name || "").localeCompare(b.legal_name || "")),
    [clients],
  );

  return (
    <Shell title="Online Dispute" subtitle="Prepare & file FTC / CFPB complaints — human presses submit" actions={<span />}>
      <div className="banner-warning banner-rail" role="note" style={{ marginBottom: 16 }}>
        <p className="small" style={{ margin: 0 }}>
          <strong>Human-in-the-loop by design.</strong> The app drafts each filing and stages it to the
          agency's review screen — <strong>a specialist presses Submit at the portal</strong>. Nothing is
          ever filed automatically (CROA).
        </p>
      </div>

      {/* PREPARE */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-header">
          <div><h2><FileText size={18} aria-hidden="true" /> Prepare a filing</h2></div>
        </div>
        <div className="form-row">
          <label className="form-field">
            <span>Client</span>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Select a client…</option>
              {clientOptions.map((c) => (
                <option key={c.id} value={c.id}>{c.legal_name || c.id.slice(0, 8)}</option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span>Filing type</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as FilingKind)}>
              <option value="cfpb_complaint">CFPB complaint</option>
              <option value="ftc_identity_theft">FTC report (identity theft)</option>
            </select>
          </label>
          <button className="btn-primary" onClick={onPrepare} disabled={preparing || !clientId}>
            {preparing ? "Preparing…" : "Prepare draft"}
          </button>
        </div>
        {err && <p className="error-text small" style={{ marginTop: 8 }}>{err}</p>}
        <p className="muted small" style={{ margin: "10px 0 0" }}>
          CFPB complaints are prepared against <strong>all three bureaus in one complaint</strong> (each bureau's
          bundle attached), using your standard narrative. Review and edit before filing.
        </p>
      </div>

      {/* OPEN FILING — review & file */}
      {open && (
        <div className="panel" style={{ marginBottom: 16, borderColor: "var(--ne-red)" }}>
          <div className="panel-header">
            <div>
              <h2><ShieldCheck size={18} aria-hidden="true" /> {open.kind === "cfpb_complaint" ? "CFPB complaint" : open.kind === "cfpb_collector_complaint" ? "CFPB complaint against collector" : "FTC report"} — {open.client_name || open.client_id.slice(0, 8)}</h2>
            </div>
            <StatusPill status={open.draft_status === "submitted" ? "submitted" : "drafting"} />
          </div>

          {blockers.length > 0 && (
            <div className="banner-warning" role="note" style={{ marginBottom: 12 }}>
              <p className="small" style={{ margin: 0, fontWeight: 600 }}><AlertTriangle size={14} aria-hidden="true" /> Not ready to file:</p>
              <ul className="small" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {blockers.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          )}

          {open.kind === "cfpb_complaint" && draft && (
            <>
              <div className="identity-grid" style={{ marginBottom: 12 }}>
                <div className="field-pair"><span className="field-label">Bureaus</span><span>{(draft.bureaus ?? []).map((b) => BUREAU_LABEL[b] ?? b).join(", ") || "—"}</span></div>
                <div className="field-pair"><span className="field-label">CFPB account on file</span><span>{draft.cfpb_account_on_file ? "Yes" : "No"} {open.cfpb_login_email ? `(${open.cfpb_login_email})` : ""}</span></div>
                <div className="field-pair"><span className="field-label">SSN last-4 on file</span><span>{draft.client_identity?.ssn_last4_on_file ? "Yes" : "No"}</span></div>
                <div className="field-pair"><span className="field-label">DOB on file</span><span>{draft.client_identity?.dob_on_file ? "Yes" : "No"}</span></div>
              </div>

              <label className="form-field">
                <span>Narrative (used in both "What happened?" and "Fair resolution")</span>
                <textarea value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={5}
                  disabled={open.draft_status === "submitted"} />
              </label>
              <div style={{ margin: "6px 0 14px" }}>
                <button className="btn-secondary" onClick={saveNarrative} disabled={saving || open.draft_status === "submitted"}>Save narrative</button>
              </div>

              <span className="field-label">Attachments (per-bureau bundle)</span>
              <div className="table-list" style={{ marginBottom: 14 }}>
                {(draft.attachments ?? []).length === 0 && <p className="muted small">No bundle available — generate a dispute round first.</p>}
                {(draft.attachments ?? []).map((a) => (
                  <div className="table-row" key={a.bureau}>
                    <span>{BUREAU_LABEL[a.bureau] ?? a.bureau}</span>
                    <span>Round {a.round_number}</span>
                    <span className="muted small">{a.filename}</span>
                    <a className="btn-secondary" href={a.bundle_path} target="_blank" rel="noreferrer"><Download size={14} aria-hidden="true" /> Bundle</a>
                  </div>
                ))}
              </div>

              <span className="field-label">Portal checklist</span>
              <ol className="small" style={{ margin: "4px 0 14px", paddingLeft: 18, lineHeight: 1.7 }}>
                {(draft.checklist ?? []).map((c, i) => <li key={i}>{c}</li>)}
              </ol>

              {blockers.length === 0 && (
                <div className="banner-warning" role="note" style={{ marginBottom: 12 }}>
                  <p className="small" style={{ margin: 0 }}>
                    ⚡ <strong>Faster:</strong> with the <strong>NX Dispute Executor</strong> browser extension,
                    open the CFPB portal, log into the client's account, and it auto-fills all of this to the
                    review screen — you just press <strong>Submit</strong>. (Prepare here first, then run it from
                    the portal.)
                  </p>
                </div>
              )}
            </>
          )}

          {open.kind === "cfpb_collector_complaint" && open.draft_content && (() => {
            const cd = open.draft_content as unknown as CollectorComplaintDraft;
            const yn = (v: boolean | null | undefined) => (v == null ? "Not asked" : v ? "Yes" : "No");
            return (
              <>
                {(cd.warnings ?? []).map((w) => (
                  <div key={w} className="banner-warning" role="note" style={{ marginBottom: 10 }}>
                    <p className="small" style={{ margin: 0 }}>{collectorWarningText(w)}</p>
                  </div>
                ))}
                <div className="identity-grid" style={{ marginBottom: 12 }}>
                  <div className="field-pair"><span className="field-label">Company (collection agency)</span><span>{cd.company?.name_as_reported || "—"}</span></div>
                  <div className="field-pair"><span className="field-label">Account number (enter in its own field only)</span><span>{cd.account_number_as_reported || "—"}</span></div>
                  <div className="field-pair"><span className="field-label">Product</span><span>{cd.wizard?.product} → {cd.wizard?.sub_product}</span></div>
                  <div className="field-pair"><span className="field-label">Issue</span><span>{cd.wizard?.issue} → {cd.wizard?.sub_issue}</span></div>
                  <div className="field-pair"><span className="field-label">Already tried to fix it?</span><span>{yn(cd.wizard?.tried_to_fix)}</span></div>
                  <div className="field-pair"><span className="field-label">Requested information?</span><span>{yn(cd.wizard?.requested_information)}</span></div>
                  <div className="field-pair"><span className="field-label">Did they provide it?</span><span>{yn(cd.wizard?.company_provided_information)}</span></div>
                  <div className="field-pair"><span className="field-label">CFPB account on file</span><span>{cd.cfpb_account_on_file ? "Yes" : "No"} {open.cfpb_login_email ? `(${open.cfpb_login_email})` : ""}</span></div>
                </div>
                <p className="muted small" style={{ margin: "0 0 12px" }}>
                  The yes/no answers come from this client's file (validation letter mailed{cd.facts?.validation_mailed_at ? ` ${new Date(cd.facts.validation_mailed_at).toLocaleDateString()}` : ": no"}; reply logged: {cd.facts?.response_logged ? "yes" : "no"}). Enter them exactly as shown.
                </p>
                <div style={{ margin: "0 0 12px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <button className="btn-secondary" onClick={refreshCollectorFacts} disabled={saving || open.draft_status === "submitted"} data-testid="refresh-collector-facts">
                    {saving ? "Refreshing…" : "Refresh from client file"}
                  </button>
                  <span className="muted small">Use after mailing the validation letter or logging the collector's reply. The client reviews the updated complaint again.</span>
                </div>
                <span className="field-label">What happened (written by the system from the file — copy exactly; it has no account number, SSN or date of birth because the CFPB can publish it)</span>
                <p className="small" style={{ whiteSpace: "pre-wrap", margin: "4px 0 14px" }}>{cd.narrative}</p>
                <span className="field-label">Fair resolution</span>
                <p className="small" style={{ whiteSpace: "pre-wrap", margin: "4px 0 14px" }}>{cd.resolution}</p>
                {(cd.attachments ?? []).length > 0 && (
                  <>
                    <span className="field-label">Attachments</span>
                    <div className="table-list" style={{ marginBottom: 14 }}>
                      {cd.attachments.map((a) => (
                        <div className="table-row" key={a.path}>
                          <span className="muted small">{a.filename}</span>
                          <a className="btn-secondary" href={a.path} target="_blank" rel="noreferrer"><Download size={14} aria-hidden="true" /> Download</a>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                <span className="field-label">Portal checklist</span>
                <ol className="small" style={{ margin: "4px 0 14px", paddingLeft: 18, lineHeight: 1.7 }}>
                  {(cd.checklist ?? []).map((c, i) => <li key={i}>{c}</li>)}
                </ol>
              </>
            );
          })()}

          {open.kind === "ftc_identity_theft" && (
            <>
              <div className="identity-grid" style={{ marginBottom: 12 }}>
                <div className="field-pair"><span className="field-label">Name</span><span>{[ftcDraft.identity?.first_name, ftcDraft.identity?.last_name].filter(Boolean).join(" ") || "—"}</span></div>
                <div className="field-pair"><span className="field-label">Phone (gets the code)</span><span>{ftcPhone || ftcDraft.identity?.phone || "—"}</span></div>
                <div className="field-pair"><span className="field-label">Email</span><span>{ftcDraft.identity?.email || "—"}</span></div>
                <div className="field-pair"><span className="field-label">DOB on file</span><span>{ftcDraft.identity?.dob_year ? "Yes" : "No"}</span></div>
              </div>

              {!ftcRef && !ftcAwaiting && (
                <>
                  {((ftcDraft.suggested_items ?? []) as any[]).length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                      <span className="field-label">Suggested from the report analysis — tap to add</span>
                      <p className="muted small" style={{ margin: "2px 0 8px" }}>
                        Auto-detected collections, charge-offs, and unrecognized hard inquiries. Only add the ones
                        the client confirms are identity theft — the attestation below still applies to every item.
                      </p>
                      <div className="table-list">
                        {(ftcDraft.suggested_items as any[]).map((s, i) => {
                          const added = ftcItems.some((it) => it.company === s.company && (it.account_number || "") === (s.account_number || ""));
                          return (
                            <div className="table-row table-row--wide" key={i}>
                              <span className="pill" style={{ minWidth: 86, textAlign: "center" }}>
                                {s.source === "collection" ? "Collection" : s.source === "charge_off" ? "Charge-off" : "Inquiry"}
                              </span>
                              <strong style={{ flex: "1 1 140px" }}>{s.company}</strong>
                              <span className="muted small" style={{ flex: "2 1 200px" }}>
                                {[s.account_number ? `acct …${s.account_number}` : null, s.amount ? `$${s.amount}` : null,
                                  s.opened_mm && s.opened_yy ? `${s.opened_mm}/${s.opened_yy}` : null].filter(Boolean).join(" · ") || s.reason}
                              </span>
                              <button className="btn-secondary" disabled={added}
                                aria-label={`Add ${s.company} to the FTC report`}
                                onClick={() => setFtcItems((prev) => [...prev, { company: s.company, opened_mm: s.opened_mm, opened_yy: s.opened_yy, amount: s.amount, account_number: s.account_number }])}>
                                {added ? "✓ Added" : "+ Add"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <button className="btn-secondary" style={{ marginTop: 8 }}
                        onClick={() => setFtcItems((prev) => {
                          const next = [...prev];
                          for (const s of (ftcDraft.suggested_items as any[])) {
                            if (!next.some((it) => it.company === s.company && (it.account_number || "") === (s.account_number || ""))) {
                              next.push({ company: s.company, opened_mm: s.opened_mm, opened_yy: s.opened_yy, amount: s.amount, account_number: s.account_number });
                            }
                          }
                          return next;
                        })}>
                        + Add all suggested
                      </button>
                    </div>
                  )}

                  <span className="field-label">Fraudulent accounts to report (opened by the thief)</span>
                  <div className="table-list" style={{ marginBottom: 10 }}>
                    {ftcItems.length === 0 && <p className="muted small">No accounts yet — add each account you're reporting as identity theft.</p>}
                    {ftcItems.map((it, i) => (
                      <div className="table-row table-row--wide" key={i}>
                        <input placeholder="Company" value={it.company} onChange={(e) => updItem(i, { company: e.target.value })} style={{ flex: "1 1 150px" }} />
                        <input placeholder="Opened MM" value={it.opened_mm || ""} onChange={(e) => updItem(i, { opened_mm: e.target.value })} style={{ width: 78 }} />
                        <input placeholder="Opened YYYY" value={it.opened_yy || ""} onChange={(e) => updItem(i, { opened_yy: e.target.value })} style={{ width: 90 }} />
                        <input placeholder="Est. $" value={it.amount || ""} onChange={(e) => updItem(i, { amount: e.target.value })} style={{ width: 80 }} />
                        <input placeholder="Acct #" value={it.account_number || ""} onChange={(e) => updItem(i, { account_number: e.target.value })} style={{ width: 110 }} />
                        <button className="btn-secondary" onClick={() => removeItem(i)} aria-label={`Remove account ${i + 1}`}>✕</button>
                      </div>
                    ))}
                  </div>
                  <button className="btn-secondary" onClick={addItem} style={{ marginBottom: 12 }}>+ Add account</button>

                  <label className="form-field" style={{ maxWidth: 280 }}>
                    <span>Phone override (optional — e.g. your own phone for a test)</span>
                    <input value={ftcPhone} onChange={(e) => setFtcPhone(e.target.value)} placeholder={ftcDraft.identity?.phone || "e.g. 555-123-4567"} />
                  </label>

                  <label className="small" style={{ display: "flex", gap: 8, alignItems: "flex-start", margin: "10px 0 12px" }}>
                    <input type="checkbox" checked={ftcAttest} onChange={(e) => setFtcAttest(e.target.checked)} style={{ marginTop: 3 }} />
                    <span>The client has attested they are a victim of identity theft and authorizes this FTC report. It is signed under penalty of perjury.</span>
                  </label>

                  <button className="btn-primary" onClick={ftcStart} disabled={ftcBusy || !ftcAttest || ftcItems.length === 0}>
                    {ftcBusy ? "Starting… (driving IdentityTheft.gov)" : "Start FTC report — send code"}
                  </button>
                  <p className="muted small" style={{ marginTop: 8 }}>
                    This drives the FTC site on the server and texts a one-time code to the phone above. You relay the code below to finish — nothing is submitted to CFPB here.
                  </p>
                </>
              )}

              {ftcAwaiting && !ftcRef && (
                <div className="banner-warning" role="note" style={{ marginTop: 8 }}>
                  <p className="small" style={{ margin: "0 0 8px" }}>📱 {ftcMsg || "A verification code was texted to the phone. Enter it to finish the report."}</p>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                    <label className="form-field" style={{ maxWidth: 170 }}>
                      <span>Verification code</span>
                      <input value={ftcCode} onChange={(e) => setFtcCode(e.target.value)} placeholder="code from text" />
                    </label>
                    <button className="btn-primary" onClick={ftcVerify} disabled={ftcBusy || !ftcCode}>
                      {ftcBusy ? "Finishing…" : "Verify & finish report"}
                    </button>
                  </div>
                </div>
              )}

              {ftcRef && (
                <div className="banner-success" role="note" style={{ marginTop: 8 }}>
                  <p className="small" style={{ margin: 0 }}>
                    ✅ FTC Identity Theft Report finalized — reference <strong>{ftcRef}</strong>.{" "}
                    <a href={`/api/filings/${open.id}/ftc/report`} target="_blank" rel="noreferrer"><Download size={13} aria-hidden="true" /> Download report PDF</a>
                  </p>
                </div>
              )}

              {ftcMsg && !ftcAwaiting && !ftcRef && (
                <p className="small" style={{ color: "var(--ne-red)", marginTop: 8 }}>{ftcMsg}</p>
              )}
            </>
          )}

          {/* FILE actions */}
          {open.draft_status !== "submitted" ? (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", borderTop: "1px solid var(--ne-line)", paddingTop: 14 }}>
              <button className="btn-secondary" onClick={genLink} disabled={saving}><Link2 size={14} aria-hidden="true" /> Generate client review link</button>
              <label className="form-field" style={{ flex: "1 1 200px" }}>
                <span>Confirmation # (after a human submits)</span>
                <input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} placeholder="e.g. 260713-12345678" />
              </label>
              <button className="btn-primary" onClick={markFiled} disabled={saving}><Send size={14} aria-hidden="true" /> Mark filed &amp; log</button>
            </div>
          ) : (
            <p className="small" style={{ borderTop: "1px solid var(--ne-line)", paddingTop: 14, margin: 0 }}>
              ✅ Logged as submitted{open.confirmation_number ? ` — confirmation ${open.confirmation_number}` : ""}
              {open.submitted_at ? ` on ${new Date(open.submitted_at).toLocaleString()}` : ""}.
            </p>
          )}
          {linkUrl && (
            <p className="small" style={{ marginTop: 10 }}>
              Client review link: <a href={linkUrl} target="_blank" rel="noreferrer">{linkUrl}</a> (14-day, single-use)
            </p>
          )}
        </div>
      )}

      {/* HISTORY + DOCS */}
      <div className="lower-grid">
        <div className="panel">
          <div className="panel-header"><div><h2><Landmark size={18} aria-hidden="true" /> Filing history</h2></div></div>
          {loading ? <p className="muted">Loading…</p> : (
            <div className="table-list">
              {filings.length === 0 && <p className="muted small">No filings yet.</p>}
              {filings.map((f) => (
                <div className="table-row" key={f.id} style={{ cursor: "pointer" }} onClick={() => openFiling(f.id)}
                  role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && openFiling(f.id)}>
                  <strong>{f.client_name || f.client_id.slice(0, 8)}</strong>
                  <span>{f.kind === "cfpb_complaint" ? "CFPB" : f.kind === "cfpb_collector_complaint" ? "CFPB · collector" : f.kind === "ftc_identity_theft" ? "FTC" : "Bureau"}</span>
                  <span>{statusLabel(f.draft_status)}</span>
                  <span className="muted small">{f.confirmation_number || (f.submitted_at ? new Date(f.submitted_at).toLocaleDateString() : "—")}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel-header"><div><h2><ShieldCheck size={18} aria-hidden="true" /> Signed compliance documents</h2></div></div>
          {loading ? <p className="muted">Loading…</p> : (
            <div className="table-list">
              {docs.length === 0 && <p className="muted small">No signed documents on file.</p>}
              {docs.map((d) => (
                <div className="table-row" key={d.id}>
                  <strong>{d.client_name || d.client_id.slice(0, 8)}</strong>
                  <span>{d.kind.replace(/_/g, " ")}</span>
                  <StatusPill status={d.signed_at ? "signed" : "unsigned"} />
                  <span className="muted small">{d.signed_at ? new Date(d.signed_at).toLocaleDateString() : "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
